// ruleMatcher.js
import { collection, query, where, getDocs, addDoc, updateDoc, doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import { auth } from "./firebase";

export async function fetchUserRules() {
  const user = auth.currentUser;
  if (!user) return [];

  const rulesRef = collection(db, "rules");
  const q = query(rulesRef, where("userId", "==", user.uid));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
}

export function matchTransactionToRule(transaction, rules) {
  const txVendor = (transaction.vendor || "").toLowerCase().trim();
  const txAmount = transaction.amount;
  const txPlaidCategory = Array.isArray(transaction.category)
    ? (transaction.category[0] || "").toLowerCase().trim()
    : (transaction.bankCategory || "").toLowerCase().trim();

  let txDay = null;
  if (transaction.date instanceof Date) {
    txDay = transaction.date.getDate();
  } else if (transaction.date?.seconds) {
    txDay = new Date(transaction.date.seconds * 1000).getDate();
  }

  let matchingRules = [];

  for (const rule of rules) {
    const ruleVendor = (rule.vendor || "").toLowerCase().trim();
    const vendorMatchType = rule.vendorMatchType || "exact";
    const amountMatchType = rule.amountMatchType || "exact";
    const amountTolerance = Number(rule.amountMatchValue || 0);
    const dateMatchType = rule.dateMatchType || "exact";
    const dateTolerance = Number(rule.dateMatchValue || 0);
    const ruleDay = rule.date != null ? Number(rule.date) : null;
    const rulePlaidCategory = (rule.plaidCategory || "").toLowerCase().trim();

    // Vendor match
    let vendorMatch = false;
    if (vendorMatchType === "exact") vendorMatch = txVendor === ruleVendor;
    else if (vendorMatchType === "contains") vendorMatch = txVendor.includes(ruleVendor);
    else if (vendorMatchType === "startsWith") vendorMatch = txVendor.startsWith(ruleVendor);
    if (!vendorMatch) continue;

    // Amount match (if present)
    if (rule.amount != null) {
      const ruleAmt = Number(rule.amount);
      const absTxAmount = Math.abs(txAmount);
      const absRuleAmt = Math.abs(ruleAmt);

      if (amountMatchType === "exact" && absTxAmount !== absRuleAmt) continue;
      if (amountMatchType === "range" && Math.abs(absTxAmount - absRuleAmt) > amountTolerance) continue;
    }

    // Date match (if present)
    if (ruleDay != null && txDay != null) {
      if (dateMatchType === "exact" && txDay !== ruleDay) continue;
      if (dateMatchType === "range") {
        const dayDiff = Math.abs(txDay - ruleDay);
        const wrappedDiff = 30 - Math.max(txDay, ruleDay) + Math.min(txDay, ruleDay); // assumes 30-day max
        const effectiveDiff = Math.min(dayDiff, wrappedDiff);

        if (effectiveDiff > dateTolerance) continue;
      }
    }

    // Plaid category match
    if (rule.plaidCategory) {
      const normalize = (str) =>
        str.toLowerCase().replace(/[^a-z\s]/g, "").split(/\s+/).filter(Boolean);

      const ruleWords = normalize(rulePlaidCategory);
      const txWords = normalize(txPlaidCategory);

      const allMatch = ruleWords.every((word) => txWords.includes(word));
      if (!allMatch) continue;
    }

    matchingRules.push(rule);
  }

  // Most specific rule wins (more fields defined)
  matchingRules.sort((a, b) => {
    const definedCount = (r) => ["vendor", "amount", "date", "plaidCategory"].filter(k => r[k] != null).length;
    return definedCount(b) - definedCount(a);
  });

    const best = matchingRules[0];
  if (!best) return null;

  return {
    category: best.category,
    subcategory: best.subcategory || null,
    source: "rule",
    type: "rule",
    ruleId: best.id || null,   // ← NEW: carry the Firestore doc id forward
  };
}

export async function saveUserRule({
  userId,
  vendor,
  category,
  subcategory,
  amount,
  plaidCategory,
  amountMatchType,
  amountMatchValue,
  vendorMatchType,
  date,
  dateMatchType,
  dateMatchValue
}) {
  const rulesRef = collection(db, "rules");

  const q = query(
    rulesRef,
    where("userId", "==", userId),
    where("vendor", "==", vendor),
    where("category", "==", category || ""),
    where("subcategory", "==", subcategory || ""),
    where("amount", "==", amount || null),
    where("plaidCategory", "==", plaidCategory || "")
  );

  const snapshot = await getDocs(q);

  if (!snapshot.empty) {
    // Update existing rule
    const ruleDoc = snapshot.docs[0];
    const updatedRule = {
      category,
      subcategory,
      plaidCategory,
      updatedAt: new Date().toISOString(),
    };

    if (amount !== undefined && amount !== null && amount !== "") {
      updatedRule.amount = Number(amount);
      updatedRule.amountMatchType = amountMatchType || "exact";
      updatedRule.amountMatchValue = Number(amountMatchValue || 0);
    }

    if (date !== undefined && date !== null && date !== "") {
      updatedRule.date = Number(date);
      updatedRule.dateMatchType = dateMatchType || "exact";
      updatedRule.dateMatchValue = Number(dateMatchValue || 0);
    }

    if (vendorMatchType) {
      updatedRule.vendorMatchType = vendorMatchType;
    }

    await updateDoc(doc(db, "rules", ruleDoc.id), updatedRule);
    return ruleDoc.id; // ← return the existing id
  } else {
    // Create new rule
    const newRule = {
      userId,
      vendor,
      category,
      subcategory,
      plaidCategory,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(), // ← also stamp updatedAt on create
    };

    if (amount !== undefined && amount !== null && amount !== "") {
      newRule.amount = Number(amount);
      newRule.amountMatchType = amountMatchType || "exact";
      newRule.amountMatchValue = Number(amountMatchValue || 0);
    }

    if (date !== undefined && date !== null && date !== "") {
      newRule.date = Number(date);
      newRule.dateMatchType = dateMatchType || "exact";
      newRule.dateMatchValue = Number(dateMatchValue || 0);
    }

    if (vendorMatchType) {
      newRule.vendorMatchType = vendorMatchType;
    }

    const ref = await addDoc(rulesRef, newRule);
    return ref.id; // ← return the new id
  }
}

export async function getRuleById(ruleId) {
  const snap = await getDoc(doc(db, "rules", ruleId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function updateRuleById(ruleId, updates) {
  await updateDoc(doc(db, "rules", ruleId), {
    ...updates,
    updatedAt: new Date().toISOString(),
  });
}
